// src/pages/Profile.js
import React, { useEffect, useMemo, useState } from "react";
import { auth, db, ref, onValue, update } from "../firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";
import { isPaidPlan, isPlanExpired } from "../utils/examAccess";
import { useLanguage } from "../i18n";
import {
  buildUserProfilePatch,
  getUserProfile,
  normalizeLanguage,
} from "../utils/userProfile";
import {
  taxonomyExamsForState,
  taxonomyStatesWithActiveExams,
} from "../utils/taxonomy";

export default function Profile() {
  const [user, loading] = useAuthState(auth);
  const [userData, setUserData] = useState({});
  const [exam, setExam] = useState("");
  const [stateName, setStateName] = useState("");
  const [preferredLanguage, setPreferredLanguage] = useState("en");
  const [taxonomyData, setTaxonomyData] = useState({});
  const [states, setStates] = useState([]);
  const [taxonomyLoading, setTaxonomyLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const navigate = useNavigate();
  const { setLang, t } = useLanguage();

  useEffect(() => {
    const taxonomyRef = ref(db, "taxonomy");
    return onValue(taxonomyRef, (snap) => {
      const taxonomy = snap.val() || {};
      setTaxonomyData(taxonomy);
      setStates(taxonomyStatesWithActiveExams(taxonomy));
      setTaxonomyLoading(false);
    });
  }, []);

  useEffect(() => {
    if (user) {
      const userRef = ref(db, `users/${user.uid}`);
      return onValue(userRef, (snap) => {
        if (snap.exists()) {
          const next = snap.val();
          setUserData(next);
          const profile = getUserProfile(next);
          setExam(profile.exam);
          setStateName(profile.state);
          setPreferredLanguage(profile.preferredLanguage || "en");
        }
      });
    } else if (!loading) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  const goToPlans = () => navigate("/plans");

  const examOptions = useMemo(() => {
    const fromTaxonomy = taxonomyExamsForState(taxonomyData, stateName);
    if (!exam || fromTaxonomy.includes(exam)) return fromTaxonomy;
    return [...fromTaxonomy, exam].sort((a, b) => a.localeCompare(b));
  }, [taxonomyData, stateName, exam]);

  const stateOptions = useMemo(() => {
    if (!stateName || states.includes(stateName)) return states;
    return [...states, stateName].sort((a, b) => a.localeCompare(b));
  }, [states, stateName]);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!user?.uid) return;

    const cleanExam = exam.trim();
    const cleanState = stateName.trim();
    const cleanPreferredLanguage = normalizeLanguage(preferredLanguage);

    if (!cleanExam || !cleanState || !cleanPreferredLanguage) {
      setSaveError("Please fill exam, state and preferred language.");
      setSaveSuccess("");
      return;
    }

    setSaving(true);
    setSaveError("");
    setSaveSuccess("");
    try {
      await update(
        ref(db, `users/${user.uid}`),
        buildUserProfilePatch({
          exam: cleanExam,
          state: cleanState,
          preferredLanguage: cleanPreferredLanguage,
        })
      );
      setLang(cleanPreferredLanguage);
      setSaveSuccess("Profile updated.");
    } catch (err) {
      setSaveError("Could not update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="center">Loading...</div>;
  if (!user) return null;

  const paid = isPaidPlan(userData.userPlan);
  const expired = paid && isPlanExpired(userData.planEndDate);
  const showUpgrade = !paid || expired;

  const planLabel = paid
    ? expired
      ? `${userData.userPlan} (${t("profile_plan_expired")})`
      : userData.userPlan
    : userData.userPlan || "-";

  return (
    <div
      className="container"
      style={{ padding: "40px 20px", maxWidth: "600px", margin: "0 auto" }}
    >
      <h2 className="page-title" style={{ marginBottom: "20px" }}>
        {t("profile_title")}
      </h2>
      <div
        className="card"
        style={{
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
        }}
      >
        <p>
          <strong>{t("profile_name")}:</strong> {userData.userName || "-"}
        </p>
        <p>
          <strong>{t("profile_email")}:</strong> {userData.userEmail || "-"}
        </p>
        <p>
          <strong>{t("profile_mobile")}:</strong> {userData.mobile || "-"}
        </p>
        <p>
          <strong>State:</strong> {userData.userState || userData.state || "-"}
        </p>
        <p>
          <strong>Exam:</strong> {userData.userExam || userData.exam || "-"}
        </p>
        <p>
          <strong>Preferred Language:</strong>{" "}
          {userData.preferredLanguage ||
            userData.preferred_language ||
            userData.language ||
            userData.lang ||
            "-"}
        </p>
        <form onSubmit={handleProfileSave} style={{ marginTop: 14 }}>
          <div style={{ display: "grid", gap: 10 }}>
            <select
              value={stateName}
              onChange={(e) => setStateName(e.target.value)}
              disabled={taxonomyLoading}
              required
            >
              <option value="">{taxonomyLoading ? "Loading states..." : "Select State"}</option>
              {stateOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <select
              value={exam}
              onChange={(e) => setExam(e.target.value)}
              disabled={taxonomyLoading || !stateName}
              required
            >
              <option value="">{taxonomyLoading ? "Loading exams..." : "Select Exam"}</option>
              {examOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <select
              value={preferredLanguage}
              onChange={(e) => setPreferredLanguage(e.target.value)}
              required
            >
              <option value="en">English</option>
              <option value="hi">Hindi</option>
            </select>
            <button type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save profile"}
            </button>
          </div>
        </form>
        {saveError ? (
          <p style={{ color: "#991b1b", marginTop: 10, marginBottom: 0 }}>{saveError}</p>
        ) : null}
        {saveSuccess ? (
          <p style={{ color: "#166534", marginTop: 10, marginBottom: 0 }}>{saveSuccess}</p>
        ) : null}
        <p>
          <strong>{t("profile_plan")}:</strong>{" "}
          <span
            style={{
              color: expired ? "#991b1b" : "inherit",
              fontWeight: expired ? 800 : "inherit",
            }}
          >
            {planLabel}
          </span>
        </p>
        <p>
          <strong>{t("profile_plan_start")}:</strong>{" "}
          {userData.planStartDate
            ? new Date(userData.planStartDate).toLocaleString()
            : "-"}
        </p>
        <p>
          <strong>{t("profile_plan_end")}:</strong>{" "}
          {userData.planEndDate
            ? new Date(userData.planEndDate).toLocaleString()
            : "-"}
        </p>

        {showUpgrade && (
          <div style={{ marginTop: "20px", paddingTop: "16px", borderTop: "1px solid #e2e8f0" }}>
            <p style={{ margin: "0 0 12px", color: "#475569", fontSize: 14 }}>
              {expired
                ? t("profile_expired_line")
                : t("profile_unlock_line")}
            </p>
            <button
              type="button"
              onClick={goToPlans}
            >
              {expired
                ? t("profile_renew_btn")
                : t("profile_upgrade_btn")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
